import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Lock, 
  Download, 
  UserX, 
  Ban, 
  Clock, 
  CheckCircle2, 
  Sparkles 
} from 'lucide-react';

const privacyPillars = [
  {
    icon: Clock,
    badge: 'Purge Automatique',
    title: 'Durées de conservation limitées',
    description: 'Les sessions expirent automatiquement selon leur durée configurée. L’historique de localisation enfant est purgé après 30 jours et les journaux d’audit après 365 jours.',
    highlight: 'Purge automatique documentée',
    color: '#38bdf8'
  },
  {
    icon: Ban,
    badge: 'Éthique Absolue',
    title: 'Pas de revente de données',
    description: 'Vos données personnelles ne sont ni vendues, ni louées, ni cédées à des tiers. Aucun traceur publicitaire ou courtier de données n\'est intégré.',
    highlight: '0% Revente / 0% Régie Pub',
    color: '#f43f5e'
  },
  {
    icon: Lock,
    badge: 'Confidentialité Totale',
    title: 'Transmission sécurisée',
    description: 'Les accès aux sessions sont protégés par authentification et des liens de partage à capacité limitée. Les données nécessaires au service sont traitées par nos serveurs.',
    highlight: 'Accès contrôlé',
    color: '#f2b807'
  },
  {
    icon: Download,
    badge: 'Transparence RGPD',
    title: 'Droit d’accès et d’export',
    description: 'Vous pouvez demander l’accès, la rectification, l’effacement ou l’export de vos données personnelles depuis les parcours prévus par le service.',
    highlight: 'Droits RGPD accessibles',
    color: '#10b981'
  },
  {
    icon: UserX,
    badge: 'Souveraineté Utilisateur',
    title: 'Suppression de compte sécurisée',
    description: 'La suppression est programmée avec un délai de grâce de 14 jours et peut être annulée par une reconnexion ou via le lien envoyé par e-mail.',
    highlight: 'Effacement définitif après délai',
    color: '#a855f7'
  }
];

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 25 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const DataPrivacy = () => {
  return (
    <section 
      id="data-privacy" 
      className="section-padding bg-glow-container" 
      style={{
        background: 'radial-gradient(circle at 50% 30%, #0d1f38 0%, #060d1a 70%, #040810 100%)',
        position: 'relative'
      }}
    >
      {/* Decorative Blur Spheres */}
      <div 
        className="bg-glow-sphere" 
        style={{ 
          width: '35vw', 
          height: '35vw', 
          background: 'var(--mga-gold)', 
          top: '20%', 
          left: '-5%',
          opacity: 0.08 
        }} 
      />
      <div 
        className="bg-glow-sphere" 
        style={{ 
          width: '40vw', 
          height: '40vw', 
          background: '#38bdf8', 
          bottom: '10%', 
          right: '-5%',
          opacity: 0.08 
        }} 
      />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
        
        {/* Header */}
        <div className="text-center" style={{ marginBottom: '60px' }}>
          <motion.span 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="badge-gold"
            style={{ gap: '8px' }}
          >
            <ShieldCheck size={16} color="var(--mga-gold)" />
            Vos Données & Vie Privée
          </motion.span>

          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            style={{
              fontSize: 'clamp(2rem, 4vw, 2.9rem)',
              color: 'var(--mga-white)',
              marginTop: '12px',
              marginBottom: '18px',
              lineHeight: 1.2
            }}
          >
            Vos données vous appartiennent. <br />
            <span style={{ 
              background: 'linear-gradient(135deg, var(--mga-gold) 0%, var(--mga-gold-light) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Elles restent strictement confidentielles.
            </span>
          </motion.h2>

          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            style={{
              maxWidth: '720px',
              margin: '0 auto',
              color: 'var(--mga-slate-300)',
              fontSize: '1.08rem',
              lineHeight: '1.7'
            }}
          >
            La sécurité ne doit jamais se faire au détriment de votre vie privée. Chez <strong>MonAlerte</strong>, nous appliquons une politique de confidentialité stricte, transparente et conforme aux plus hautes normes éthiques et RGPD.
          </motion.p>
        </div>

        {/* Grid of Privacy Pillars */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '24px',
            marginBottom: '50px'
          }}
        >
          {privacyPillars.map((pillar, index) => {
            const IconComponent = pillar.icon;
            return (
              <motion.div
                key={index}
                variants={cardVariants}
                className="glass-card"
                style={{
                  padding: '32px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  background: 'rgba(11, 31, 58, 0.35)',
                  border: '1px solid rgba(255, 255, 255, 0.07)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Subtle top accent border */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: `linear-gradient(90deg, ${pillar.color} 0%, transparent 100%)`,
                  opacity: 0.8
                }} />

                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '22px'
                  }}>
                    <div style={{
                      width: '54px',
                      height: '54px',
                      borderRadius: '14px',
                      backgroundColor: `${pillar.color}15`,
                      border: `1px solid ${pillar.color}35`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: pillar.color
                    }}>
                      <IconComponent size={26} />
                    </div>

                    <span style={{
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      padding: '4px 12px',
                      borderRadius: '100px',
                      backgroundColor: `${pillar.color}15`,
                      color: pillar.color,
                      border: `1px solid ${pillar.color}30`
                    }}>
                      {pillar.badge}
                    </span>
                  </div>

                  <h3 style={{
                    color: 'var(--mga-white)',
                    fontSize: '1.3rem',
                    marginBottom: '12px',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700
                  }}>
                    {pillar.title}
                  </h3>

                  <p style={{
                    color: 'var(--mga-slate-300)',
                    fontSize: '0.96rem',
                    lineHeight: '1.65',
                    margin: 0
                  }}>
                    {pillar.description}
                  </p>
                </div>

                <div style={{
                  marginTop: '24px',
                  paddingTop: '16px',
                  borderTop: '1px dashed rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: pillar.color
                }}>
                  <CheckCircle2 size={16} />
                  <span>{pillar.highlight}</span>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Feature Highlight Box / Commitments Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-card"
          style={{
            padding: '36px 40px',
            background: 'linear-gradient(135deg, rgba(15, 39, 74, 0.6) 0%, rgba(11, 31, 58, 0.8) 100%)',
            border: '1px solid rgba(242, 184, 7, 0.25)',
            boxShadow: '0 15px 35px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '30px',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ flex: '1 1 500px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <Sparkles size={20} color="var(--mga-gold)" />
              <span style={{ 
                color: 'var(--mga-gold)', 
                fontWeight: 700, 
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Charte d'Engagement Éthique
              </span>
            </div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '10px', color: 'var(--mga-white)' }}>
              Une application conçue pour vous protéger, pas pour vous tracer.
            </h3>
            <p style={{ margin: 0, color: 'var(--mga-slate-300)', fontSize: '0.98rem' }}>
              Depuis votre smartphone jusqu'à nos serveurs souverains hébergés en France, vos droits RGPD sont intégrés nativement (*Privacy by Design*). Vous avez un contrôle absolu sur votre compte et vos données.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            <a 
              href="/politique-confidentialite" 
              className="button-secondary"
              style={{ fontSize: '0.9rem', padding: '12px 24px' }}
            >
              Lire la politique complète
            </a>
            <Link
              to="/rejoindre"
              className="button-primary"
              style={{ fontSize: '0.9rem', padding: '12px 24px' }}
            >
              Rejoindre gratuitement
            </Link>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default DataPrivacy;
